/* hooks/useNotificationsStore.jsx — ЕДИНЫЙ стор уведомлений (S11, фикс №50).
   Один источник правды для NotificationsBell и NotificationsInbox: никаких
   window.CustomEvent. Любое markRead/markAllRead/delete мгновенно отражается
   и в бейдже колокола, и в списке инбокса (общий контекст).

   Интерфейс useNotificationsStore():
     { notifications, unreadCount, loading, error,
       markRead(id), toggleRead(id, value), markAllRead(), deleteNotification(id), refetch() }

   Polling — setInterval 30с (refetch silent). Локальные правки (прочитано/удалено)
   сохраняются через refs-оверрайды, чтобы повторный fetch их не сбрасывал.
   ХЕНДОФФ: → frontend/src/hooks/useNotificationsStore.js (Zustand или Context+reducer);
   заменяет хрупкую CustomEvent-синхронизацию прода. */

import React from 'react';
import { useAuth } from '../app/providers/AuthProvider.jsx';
import { isDemoRoute } from '../screens/demo/demoAccess.jsx';
import {
  listNotifications,
  markNotificationRead,
  markNotificationUnread,
  markAllNotificationsRead,
  deleteNotification as apiDeleteNotification,
} from '../lib/api/notifications.js';
import { adaptNotification, gateNotificationDestination } from '../lib/adapters/notifications.js';

  const { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } = React;

  const Ctx = createContext(null);

  function NotificationsProvider({ children }) {
    const { workspaces, activeWorkspace } = useAuth();
    // join workspace_id → name (бэк уведомлений отдаёт только workspace_id; контекст пространства — фикс №10).
    const wsNameMap = useMemo(() => {
      const m = {};
      (workspaces || []).forEach((w) => { if (w && w.id != null) m[w.id] = w.name; });
      return m;
    }, [workspaces]);
    const wsMapRef = useRef(wsNameMap);
    useEffect(() => { wsMapRef.current = wsNameMap; }, [wsNameMap]);

    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const overrides = useRef({});  // id → is_read (оптимистичный оверрайд до ответа)
    const deleted = useRef({});    // id → true (оптимистичное скрытие до ответа DELETE)

    // применяет локальные оверрайды/скрытия поверх серверного снимка
    const merge = useCallback((server) => server
      .filter((n) => !deleted.current[n.id])
      .map((n) => (overrides.current[n.id] != null ? { ...n, is_read: overrides.current[n.id] } : n)), []);

    // Боевой apiFetch резолвится в {ok,status,data} и НЕ бросает → ветвим по res.ok (как прод-экраны).
    const fetchNow = useCallback((opts = {}) => {
      if (!opts.silent) { setLoading(true); setError(null); }
      return listNotifications({ limit: 200 })
        .then((res) => {
          if (!res.ok) { if (!opts.silent) setError({ status: res.status, message: (res.data && res.data.detail) || 'Ошибка' }); return; }
          const rows = ((res.data && res.data.results) || []).map((n) => adaptNotification(n, wsMapRef.current));
          setList(merge(rows));
          if (!opts.silent) setError(null);
        })
        .catch((err) => { if (!opts.silent) setError({ status: 500, message: String(err && err.message || err) }); })
        .finally(() => { if (!opts.silent) setLoading(false); });
    }, [merge]);

    // первичная загрузка + polling 30с (тихий refetch — без мигания loading)
    useEffect(() => {
      // Публичный демо-стенд — гость, уведомлений нет: не грузим и не поллим.
      if (isDemoRoute()) { setLoading(false); return undefined; }
      fetchNow();
      const t = setInterval(() => fetchNow({ silent: true }), 30000);
      return () => clearInterval(t);
    }, [fetchNow]);

    // workspaces грузятся асинхронно (после первого fetch) → переименовываем без рефетча.
    useEffect(() => {
      setList((l) => l.map((n) => ({ ...n, workspace_name: (n.workspace_id != null && wsNameMap[n.workspace_id]) || n.workspace_name || '' })));
    }, [wsNameMap]);

    // Персист одного перехода read-state с откатом: при сбое POST'а снимаем оверрайд,
    // возвращаем строку и тихо рефетчим (как в deleteNotification). При успехе чистим
    // оверрайд → 30-с polling может «само-вылечить» расхождение, а не маскировать его вечно.
    const persistRead = useCallback((id, next, prev) => {
      const call = next ? markNotificationRead(id) : markNotificationUnread(id);
      const revert = () => {
        if (prev === undefined) delete overrides.current[id]; else overrides.current[id] = prev;
        fetchNow({ silent: true });
      };
      call.then((res) => {
        if (res && res.ok) { if (overrides.current[id] === next) delete overrides.current[id]; }
        else if (!res || res.status !== 404) revert();  // 404 = строки нет, считаем согласованным
      }).catch(revert);
    }, [fetchNow]);

    const markRead = useCallback((id) => {
      const prev = overrides.current[id];
      overrides.current[id] = true;
      setList((l) => l.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
      persistRead(id, true, prev);
    }, [persistRead]);

    // Двусторонний read-toggle: бэк умеет и READ, и UN-READ — персистим обе стороны.
    const toggleRead = useCallback((id) => {
      setList((l) => l.map((n) => {
        if (n.id !== id) return n;
        const next = !n.is_read;
        const prev = overrides.current[id];
        overrides.current[id] = next;
        persistRead(id, next, prev);
        return { ...n, is_read: next };
      }));
    }, [persistRead]);

    const markAllRead = useCallback(() => {
      setList((l) => l.map((n) => { overrides.current[n.id] = true; return { ...n, is_read: true }; }));
      markAllNotificationsRead().then((res) => {
        if (res && res.ok) { overrides.current = {}; }  // сервер — источник правды, оверрайды не нужны
        else fetchNow({ silent: true });
      }).catch(() => fetchNow({ silent: true }));
    }, [fetchNow]);

    // Реальное удаление на бэке (DELETE /api/notifications/<id>/). Оптимистично
    // скрываем строку; при сбое — снимаем скрытие и рефетчим (откат).
    const deleteNotification = useCallback((id) => {
      deleted.current[id] = true;
      setList((l) => l.filter((n) => n.id !== id));
      apiDeleteNotification(id).then((res) => {
        if (!res.ok && res.status !== 404) {  // 404 = уже нет, считаем успехом
          delete deleted.current[id];
          fetchNow({ silent: true });
        }
      }).catch(() => {
        delete deleted.current[id];
        fetchNow({ silent: true });
      });
    }, [fetchNow]);

    const unreadCount = useMemo(() => list.filter((n) => !n.is_read).length, [list]);

    // Адрес «Перейти» на закрытый тарифом раздел (напр. /analytics) убираем под активное
    // пространство — пересчёт сразу при смене пространства, без рефетча.
    const notifications = useMemo(
      () => list.map((n) => gateNotificationDestination(n, activeWorkspace)),
      [list, activeWorkspace]);

    const value = useMemo(() => ({
      notifications, unreadCount, loading, error,
      markRead, toggleRead, markAllRead, deleteNotification, refetch: () => fetchNow(),
    }), [notifications, unreadCount, loading, error, markRead, toggleRead, markAllRead, deleteNotification, fetchNow]);

    return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
  }

  // Безопасный хук: если провайдер не смонтирован (напр. /login), вернём заглушку.
  const EMPTY = {
    notifications: [], unreadCount: 0, loading: false, error: null,
    markRead() {}, toggleRead() {}, markAllRead() {}, deleteNotification() {}, refetch() {},
  };
  function useNotificationsStore() { return useContext(Ctx) || EMPTY; }

  // metaFor(type) → {label, icon, tone} — единая семантика категорий (01-DS §2.3).
  const NOTIF_META = {
    invitation_received:     { label: 'Приглашение',     icon: 'user-add',    tone: 'accent'  },
    payment_succeeded:       { label: 'Оплата',          icon: 'credit-card', tone: 'success' },
    payment_failed:          { label: 'Оплата',          icon: 'credit-card', tone: 'danger'  },
    // Окончание подписки: «заканчивается» — предупреждение, «закончилась» — уже
    // случилось и платные разделы скоро закроются, поэтому тон тревожнее.
    subscription_expiring:   { label: 'Подписка',        icon: 'crown',       tone: 'warning' },
    subscription_expired:    { label: 'Подписка',        icon: 'crown',       tone: 'danger'  },
    gift_expiring:           { label: 'Подарок',         icon: 'gift',        tone: 'warning' },
    // История: прежние окна «за 7 дней» / «за 1 день» — строки остались в инбоксе.
    subscription_expiring_7d:{ label: 'Подписка',        icon: 'crown',       tone: 'warning' },
    subscription_expiring_1d:{ label: 'Подписка',        icon: 'crown',       tone: 'warning' },
    subscription_expiring_early:{ label: 'Подписка',     icon: 'crown',       tone: 'warning' },
    report_ready:            { label: 'Отчёт',           icon: 'reports',     tone: 'muted'   },
    price_spike_alert:       { label: 'Рост цен',        icon: 'analytics',   tone: 'warning' },
    menu_published:          { label: 'Меню',            icon: 'kitchen',     tone: 'success' },
    ttk_cost_stale:          { label: 'Себестоимость',   icon: 'percent',     tone: 'warning' },
    member_joined:           { label: 'Команда',         icon: 'user-add',    tone: 'accent'  },
    member_left:             { label: 'Команда',         icon: 'user',        tone: 'muted'   },
    // Академия: у каждого типа свой ярлык — общая «Академия» на пяти строках
    // подряд не даёт понять, что именно произошло, ещё до открытия уведомления.
    academy_lesson_opened:   { label: 'Новый урок',      icon: 'book',           tone: 'accent'  },
    academy_course_assigned: { label: 'Курс назначен',   icon: 'book',           tone: 'accent'  },
    // Глиф — credit-card, как у payment_succeeded/payment_failed: это одна
    // смысловая группа «деньги», и два разных значка внутри неё читались как
    // два разных типа события.
    academy_course_purchased:{ label: 'Курс оплачен',    icon: 'credit-card',    tone: 'success' },
    academy_homework_reviewed:{ label: 'ДЗ проверено',   icon: 'check-circle',   tone: 'success' },
    academy_homework_submitted:{ label: 'ДЗ на проверку', icon: 'file-text',     tone: 'warning' },
    academy_certificate_issued:{ label: 'Сертификат',    icon: 'graduation-cap', tone: 'success' },
  };
  function metaFor(type) { return NOTIF_META[type] || { label: 'Уведомление', icon: 'info', tone: 'muted' }; }

export { NotificationsProvider };
export { useNotificationsStore };
export { metaFor as notifMetaFor };
export { NOTIF_META };
